// ─────────────────────────────────────────────────────────────
// Company hub: wallet balance, top-up, quick links to staff &
// payroll. Lists the user's companies; if none, redirects to the
// entry fork. Wrap route in <ServiceRoute serviceKey="payroll">.
//
// FIXED — the "no companies" branch previously rendered its own
// inline create-prompt going straight to /business/new, bypassing
// CompanyEntryPage.jsx's intent fork entirely. That meant a
// zero-company user landing here (e.g. via direct link, or after
// being removed from their only company) only ever saw "Create
// Company" — never "I was invited to a company," even though that
// fork exists specifically to prevent that wrong-context dead end.
// Now redirects to /business/landing instead of rendering its own
// prompt, so CompanyEntryPage is the single place that decision gets
// made, not duplicated here.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

export default function CompanyDashboardPage() {
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [active, setActive]       = useState(null);
  const [wallet, setWallet]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [topupAmt, setTopupAmt]   = useState("");
  const [showTopup, setShowTopup] = useState(false);

  const loadCompanies = () => {
    apiClient.get("/mtrade/companies")
      .then(r => {
        if (r.data.status) {
          setCompanies(r.data.data);
          if (r.data.data.length) setActive(r.data.data[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(loadCompanies, []);

  useEffect(() => {
    if (!active) return;
    apiClient.get(`/mtrade/companies/${active.companyId}/wallet`)
      .then(r => { if (r.data.status) setWallet(r.data.data); })
      .catch(() => {});
  }, [active]);

  // FIXED — redirect to the entry fork instead of rendering an inline
  // create-only prompt. Runs once loading is done and companies is
  // confirmed empty.
  useEffect(() => {
    if (!loading && companies.length === 0) {
      navigate("/business/landing", { replace: true });
    }
  }, [loading, companies, navigate]);

  const topup = async () => {
    if (!topupAmt || Number(topupAmt) < 100) return toast.error("Minimum ₦100");
    try {
      const r = await apiClient.post(`/mtrade/companies/${active.companyId}/wallet/topup`, { amount: Number(topupAmt) });
      if (r.data.status) window.location.href = r.data.data.authorizationUrl;
      else toast.error(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Top-up failed"); }
  };

  // FIXED — covers both the genuine loading state AND the brief
  // window between "companies confirmed empty" and the redirect
  // effect actually firing, so there's no flash of an empty dashboard
  // shell before navigating away.
  if (loading || companies.length === 0) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
    </div>
  );

  const role = active?.myRole;
  const canTopup = role === "owner" || role === "finance";
  const canPayroll = role === "owner" || role === "admin";

  return (
    <div className="min-h-screen bg-bg pb-28 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <h1 className="text-primary font-bold text-lg">{active?.name}</h1>
          <button onClick={() => navigate("/business/new", { state: { isAdditional: true } })} className="text-brand text-xs font-bold">+ New</button>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-5">
        {/* Company switcher */}
        {companies.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {companies.map(c => (
              <button key={c.companyId} onClick={() => setActive(c)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                  active?.companyId === c.companyId ? "bg-brand text-on-brand" : "bg-surface text-secondary border border-border"
                }`}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Wallet — gradient kept fixed/inline by design, see file header */}
        <div className="rounded-2xl px-5 py-5" style={{ background: "linear-gradient(135deg, #0a2e1e, #0f4c35 60%, #1a6b4a)" }}>
          <p className="text-white/60 text-xs mb-1">Company Wallet</p>
          <p className="text-white font-bold text-3xl">₦{Number(wallet?.balance || 0).toLocaleString()}</p>
          <p className="text-emerald-200 text-xs mt-1 capitalize">Your role: {role}</p>
          {canTopup && (
            <button onClick={() => setShowTopup(v => !v)}
              className="mt-3 bg-white/15 hover:bg-white/25 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors">
              <i className="ri-add-line mr-1" />Top Up Wallet
            </button>
          )}
          {showTopup && canTopup && (
            <div className="mt-3 flex gap-2">
              <input type="number" value={topupAmt} onChange={e => setTopupAmt(e.target.value)} placeholder="Amount"
                className="flex-1 bg-white/90 rounded-xl px-3 py-2 text-sm text-primary outline-none placeholder:text-faint" />
              <button onClick={topup} className="bg-white text-brand-text font-bold text-sm px-4 rounded-xl">Pay</button>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => navigate(`/business/${active.companyId}/staff`)}
            className="bg-surface border border-border rounded-2xl p-4 text-left hover:border-brand-border transition-colors shadow-card">
            <i className="ri-team-line text-brand text-xl" />
            <p className="text-primary font-semibold text-sm mt-2">Staff</p>
            <p className="text-muted text-xs">Manage your team</p>
          </button>
          <button onClick={() => canPayroll ? navigate(`/business/${active.companyId}/payroll`) : toast.error("Owner or admin only")}
            className="bg-surface border border-border rounded-2xl p-4 text-left hover:border-brand-border transition-colors shadow-card">
            <i className="ri-bank-card-line text-brand text-xl" />
            <p className="text-primary font-semibold text-sm mt-2">Payroll</p>
            <p className="text-muted text-xs">Run & review payroll</p>
          </button>
          {role === "owner" && (
            <button onClick={() => navigate(`/business/${active.companyId}/members`)}
              className="bg-surface border border-border rounded-2xl p-4 text-left hover:border-brand-border transition-colors shadow-card">
              <i className="ri-shield-user-line text-brand text-xl" />
              <p className="text-primary font-semibold text-sm mt-2">Team Roles</p>
              <p className="text-muted text-xs">Admins & finance</p>
            </button>
          )}
          <button onClick={() => navigate(`/business/${active.companyId}/settings`)}
            className="bg-surface border border-border rounded-2xl p-4 text-left hover:border-brand-border transition-colors shadow-card">
            <i className="ri-settings-3-line text-brand text-xl" />
            <p className="text-primary font-semibold text-sm mt-2">Settings</p>
            <p className="text-muted text-xs">Company details</p>
          </button>
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}
